/**
 * NPC cars use business parking lots (owner's answer Q36): the generator paves a pad beside every
 * business (world/parking.ts); some cars stand there when a chunk streams in, passing traffic pulls
 * in now and then (a slow drive straight onto the slot, ignoring lanes), and a parked car leaves
 * again after a few minutes — it is put back on the kerb lane in front of the lot and drives off.
 * Parked NPC cars are ordinary vehicles: players can take them (or park in the lots themselves).
 */
import { CHUNK, RF_SIDEWALK } from '../world/generator';
import type { Chunk } from '../world/generator';
import type { Driveway } from '../world/parking';
import { forward, makeVehicle, type VehicleEntity } from './entities';
import type { Game } from './game';
import { updateVehicle, vehicleBlocked } from './physics';
import type { Rng } from '../world/rng';
import type { Vehicle, ZoneName } from '../content/types';
import { oneWayDir } from './ai';

const STAY_MIN = 90, STAY_MAX = 300, PULL_IN_CHANCE = 0.12, LOOK = 10, PARK_SPEED = 3.5, PARK_STUCK = 8;

/** Is a slot free (no vehicle within 2.2 m of its centre)? */
export function slotFree(vehicles: VehicleEntity[], sl: { cx: number; cy: number }, except?: VehicleEntity): boolean {
  for (const v of vehicles) if (v !== except && Math.hypot(v.x - sl.cx, v.y - sl.cy) < 2.2) return false;
  return true;
}

/** Chunk streamed in: fill some of its slots with standing cars (normal-width road cars of the zone). */
export function spawnParked(game: Game, c: Chunk, zone: ZoneName, rng: Rng, pool: Vehicle[], home: string): void {
  const cands = pool.filter((d) => (d.class === 'car' || d.class === 'sports') && d.size[0] <= 2.1 && !d.tags.includes('police'));
  if (!cands.length) return;
  const trailer = game.reg.get('vehicle', 'boat_trailer'), smallBoats = game.reg.all('vehicle').filter((d) => d.class === 'boat' && d.size[1] <= 8 && d.size[0] <= 2.6 && !d.tags.includes('police'));
  for (const pad of c.parking) {
    const [bx, by] = pad.key.split(',').map(Number); const b = game.world.buildingAt(bx + 0.5, by + 0.5); const boaty = !!b && b.def.tags.some((t) => t === 'marina' || t === 'boats' || t === 'boathouse'); // a marina's lot: boat trailers, some with a boat on (trailer.ts)
    for (const sl of pad.slots) {
      if (sl.cx < c.x0 || sl.cx >= c.x0 + CHUNK || sl.cy < c.y0 || sl.cy >= c.y0 + CHUNK) continue; // the neighbour chunk fills its side
      if (boaty && trailer && rng.chance(0.5) && slotFree(game.vehicles, sl)) { const t = makeVehicle(trailer, sl.cx, sl.cy, sl.angle, home); game.vehicles.push(t); const bd = smallBoats.length && rng.chance(0.5) ? rng.pick(smallBoats) : undefined; if (bd) { const bt = makeVehicle(bd, sl.cx, sl.cy, sl.angle, home); bt.onTrailer = t.id; t.cargo = bt.id; bt.alt = 0.35; game.vehicles.push(bt); } continue; }
      if (!rng.chance(0.45) || !slotFree(game.vehicles, sl)) continue;
      const def = rng.weighted(cands, (d) => (d.zones[zone] ?? 0.2) * (d.rarity ?? 1)); if (!def) continue;
      const v = makeVehicle(def, sl.cx, sl.cy, sl.angle, home);
      v.parkedAt = game.time - rng.float() * STAY_MAX; // staggered: some leave soon, some later
      game.vehicles.push(v);
    }
  }
}

/** The kerb lane in front of a pad: where a leaving car is put (a road tile 1–4 tiles past the pad's street edge, in the nose direction). */
function kerbOf(game: Game, pad: Driveway, sl: { cx: number; cy: number; angle: number }): { x: number; y: number; axis: 'v' | 'h'; dir: 1 | -1; angle: number } | undefined {
  const w = game.world;
  const [fx, fy] = forward(sl.angle);
  const half = Math.abs(fx) > 0.5 ? pad.w / 2 : pad.h / 2;
  for (let d = half + 1; d <= half + 5; d++) {
    const x = sl.cx + fx * d, y = sl.cy + fy * d;
    if (!w.roadAt(x, y) || w.roadFlags(x, y) & RF_SIDEWALK) continue;
    const hit = w.gen.net.hitsAt(Math.floor(x), Math.floor(y)).find((h) => h.road.class !== 'rail' && Math.abs(h.off) <= Math.floor(h.road.width / 2));
    if (!hit) continue;
    // drive along the road, on the near lane, away from the pad side: pick the direction that keeps the car on its own side (right-hand traffic)
    const axis = hit.axis;
    let dir: 1 | -1 = axis === 'v' ? (fx > 0 ? -1 : 1) : (fy > 0 ? 1 : -1); // pad to the west of a v road → drive north (dir -1)… keeps the near lane on our side
    const ow = oneWayDir(hit); if (ow) dir = ow;
    const angle = axis === 'v' ? (dir === -1 ? 0 : Math.PI) : dir === 1 ? Math.PI / 2 : -Math.PI / 2;
    // the lane: half a road width off the centre, on the pad's side
    const laneOff = Math.max(1, Math.floor(hit.road.width / 4));
    const lx = axis === 'v' ? hit.pos + 0.5 + (fx > 0 ? -laneOff : laneOff) : x, ly = axis === 'h' ? hit.pos + 0.5 + (fy > 0 ? -laneOff : laneOff) : y;
    return { x: lx, y: ly, axis, dir, angle };
  }
  return undefined;
}

/** Every step, for cars with an AI: passing a lot with a free slot, some pull in. */
export function considerParking(game: Game, v: VehicleEntity, rng: Rng): void {
  const ai = v.ai; if (!ai || ai.park || ai.chase || ai.turn || v.driver || v.def.size[0] > 2.1 || v.def.size[1] > 6 || v.def.class === 'bus' || v.def.class === 'train' || v.wrecked) return;
  if (Math.abs(v.speed) < 2 || v.home === 'police' || v.home === 'gang' || v.def.tags.includes('police') || v.def.tags.includes('service')) return;
  if (!rng.chance(PULL_IN_CHANCE * 0.02)) return; // ~12 % of the cars that pass a lot in a second-ish window
  const [fx, fy] = forward(v.angle);
  for (const pad of game.world.parkingIn(v.x - LOOK, v.y - LOOK, v.x + LOOK, v.y + LOOK)) {
    for (const sl of pad.slots) {
      const dx = sl.cx - v.x, dy = sl.cy - v.y, d = Math.hypot(dx, dy);
      if (d > LOOK || d < 3 || dx * fx + dy * fy < d * 0.3) continue; // ahead of us, not behind
      if (dx * -fy + dy * fx < d * 0.2) continue; // on our side of the road (right-hand traffic): no cutting across the oncoming lane
      if (!slotFree(game.vehicles, sl, v)) continue;
      // the slot must open towards us: its nose direction points back at the car (a lot on the far side of a block faces away)
      const [sx, sy] = forward(sl.angle);
      if (sx * dx + sy * dy > -d * 0.3) continue;
      ai.park = { x: sl.cx, y: sl.cy, angle: sl.angle, key: pad.key, t: 0 };
      return;
    }
  }
}

/** Drive straight onto the slot at walking pace; snap into it at the end. Returns true while parking. */
export function stepParking(game: Game, v: VehicleEntity, dt: number): boolean {
  const ai = v.ai, p = ai?.park; if (!ai || !p) return false;
  const w = game.world;
  p.t += dt;
  const dx = p.x - v.x, dy = p.y - v.y, d = Math.hypot(dx, dy);
  if (d < 0.9 || (d < 2 && Math.abs(v.speed) < 0.3)) {
    // in: settle into the slot
    if (!slotFree(game.vehicles, { cx: p.x, cy: p.y }, v) || vehicleBlocked(w, v, p.x, p.y, p.angle, 'drive')) { ai.park = undefined; return false; }
    v.x = p.x; v.y = p.y; v.angle = p.angle; v.speed = 0;
    v.ai = undefined; v.parkedAt = game.time;
    return true;
  }
  if (p.t > PARK_STUCK || !slotFree(game.vehicles, { cx: p.x, cy: p.y }, v)) { ai.park = undefined; ai.stuck = 0; return false; } // taken or stuck: back to traffic
  const want = Math.atan2(dx, -dy);
  let da = want - v.angle; while (da > Math.PI) da -= Math.PI * 2; while (da < -Math.PI) da += Math.PI * 2;
  const steer = Math.max(-1, Math.min(1, da * 1.5));
  const throttle = v.speed < PARK_SPEED ? 0.5 : v.speed > PARK_SPEED + 1 ? -0.5 : 0;
  ai.ignorePeds = 1;
  w.setMover(v.x, v.y, v.alt);
  updateVehicle(w, v, { throttle, steer, up: false, down: false }, dt);
  return true;
}

/** Parked NPC cars pull out after a while: onto the kerb lane in front of the lot, then off as traffic. */
export function stepParked(game: Game, v: VehicleEntity, rng: Rng): void {
  if (v.parkedAt === undefined || v.ai || v.driver || v.wrecked) return;
  if (game.time - v.parkedAt < STAY_MIN) return;
  if (!rng.chance(0.004)) return; // ~every 4 s on average once the minimum stay is over (stays 90–300 s)
  const pad = game.world.parkingIn(v.x - 1, v.y - 1, v.x + 1, v.y + 1)[0];
  if (!pad) { v.parkedAt = undefined; return; }
  const sl = pad.slots.reduce((a, b) => (Math.hypot(a.cx - v.x, a.cy - v.y) < Math.hypot(b.cx - v.x, b.cy - v.y) ? a : b));
  const kerb = kerbOf(game, pad, sl);
  if (!kerb) { v.parkedAt = game.time; return; }
  if (game.vehicles.some((o) => o !== v && o.parkedAt === undefined && Math.hypot(o.x - kerb.x, o.y - kerb.y) < 5)) return; // wait for a gap in the traffic (the neighbour in the next slot does not count)
  if (game.people.some((q) => !q.vehicle && Math.hypot(q.x - kerb.x, q.y - kerb.y) < 1.6)) return; // somebody actually standing in the lane (the sidewalk crowd two tiles away does not count — a downtown pad never emptied otherwise)
  v.x = kerb.x; v.y = kerb.y; v.angle = kerb.angle; v.speed = 2; v.parkedAt = undefined;
  v.ai = { axis: kerb.axis, dir: kerb.dir, wait: 0, lost: 0, blockedFor: 0, overtake: 0, stuck: 0, reverse: 0, ignorePeds: 0, redFor: 0 };
  void STAY_MAX;
}
